import { createContext, useContext, ReactNode, useCallback } from 'react';

type ThemeContextType = {
    toggleTheme: () => void;
};

type Theme = "light";
type ThemeChangeEventDetail = {
    theme: Theme;
};
type ThemeChangeEvent = CustomEvent<ThemeChangeEventDetail>;
declare global {
    interface Window {
        lighTheme: {
            getTheme: () => Theme;
            setTheme: (theme: Theme) => void;
            observe: {
                on: (listener: (event: ThemeChangeEvent) => void) => void;
                off: (listener: (event: ThemeChangeEvent) => void) => void;
            };
        };
    }
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/*
Dark theme handling is implemented as follows:

- In index.html:
    - The Mosaic dark theme stylesheet is linked via a `<link>` tag with `disabled="true"` by default.
    - A script is embedded directly in the HTML to:
        - Determine the preferred theme using the `theme:mode` key from localStorage.
        - Optionally fall back to the user's system preference (`prefers-color-scheme: dark`) when auto mode is enabled.
        - Apply the correct theme by enabling/disabling the dark theme stylesheet.
        - Dispatch a `CustomEvent` (`theme-mode-changed`) whenever the applied theme changes.
        - React to changes in system preference and storage events to synchronize the theme dynamically.
        - Expose a `window.darkTheme` object that allows other parts of the app to get/set the theme and observe changes.

- In ThemeContext.tsx (React side):
    - A `ThemeProvider` reads the current theme from `window.darkTheme.getTheme()` during initialization.
    - It listens for the `theme-mode-changed` event using the `observe.on` method and updates React state accordingly.
    - The provider exposes the current theme mode and a `toggleTheme` method to switch between dark and light modes.
    - The toggle updates localStorage, which triggers a re-application of the theme and event dispatching via the shared script.

Rationale:
- Why is the logic in index.html?
    - To ensure the correct theme is applied *before* React renders, preventing a flash of incorrect styles (FOUC).

- Why is localStorage used for theme persistence?
    - It allows theme selection to persist across browser sessions and synchronize instantly across multiple tabs using the `storage` event.

- Why expose `window.darkTheme`?
    - To provide a single source of truth for theme handling that works across plain JS, multiple browser tabs, and the React app.
*/

export const ThemeProvider = ({ children }: { children: ReactNode; }) => {


    const toggleTheme = useCallback(() => window.lighTheme.setTheme("light"), []);

    return (
        <ThemeContext.Provider value={{ toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};